import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { View, Tabs, Separator, YStack, XStack, Text } from "tamagui";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useKiosk } from "@screen/Kiosk/useKiosk";
import FirstJoinSetup from "@screen/Kiosk/components/FirstJoinSetup";
import { colors } from "@styles/colors";
import TextCustom, { TextSize } from "@components/TextCoustom";
import { zIndexes } from "@utils/zIndexLevel";
import ShareSheet from "./component/ShareSheet";
import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import GasStationInfo from "./pages/GasShop/components/GasStationInfo";
import SupplierIdentityBar from "@components/SupplierIdentityBar";
import { RiHome2Line, RiWaterFlashLine, RiLoader2Line, RiUserLine, RiFileList3Line, RiArrowLeftSLine } from "react-icons/ri";
import { GasProductTitle } from "./pages/GasShop/components/GasProductItem";
import UserProfileHeader from "./pages/Account/components/UserProfileHeader";
import { WalletBalanceHeader } from "./pages/Account/components/wallet/WalletBalanceHeader";
import { useOrder } from "@utils/store/cart/useOrder";
import { useCurrentDraftSync } from "@utils/store/cart/useCurrentDraftSync";
import { IdentitySwitchMenu, MemberCircleList } from "@components/IdentitySwitchMenu";
import { useUser } from "@utils/store/user/useUser";
import { AddressSwitchMenu } from "@components/address/AddressSwitchMenu";
import OrderListHeader from "./pages/OrderList/components/OrderListHeader";
import { track } from "@lib/tracker";
import { isPayLinkMode } from "@utils/payLink";
import { isGasSkinV2, isGasShopPath, isSkinV2Path, isHazePath, v2 } from "./pages/GasShop/skin";
import { useTrackingHero } from "@utils/store/tracking/useTrackingHero";
import { useOrderRealtimeConnection } from "@utils/realtime/useOrderRealtimeConnection";
import { isOrdersPath } from "@utils/realtime/orderRealtimeSession";
import { ORDER_ENTRY_PATH } from "@screen/Kiosk/orderEntry";


const tabButtons = [
  { keyWord: "home", path: "/home", title: "首頁", icon: RiHome2Line, actIcon: RiHome2Line },
  // 分頁本體是點單機（/kiosk）；舊 /gasShop 流程保留，進到那裡也算這個分頁
  { keyWord: "gasShop", alsoMatch: ORDER_ENTRY_PATH, path: ORDER_ENTRY_PATH, title: "瓦斯訂購", icon: RiWaterFlashLine, actIcon: RiWaterFlashLine },
  { keyWord: "orders", path: "/orders", title: "訂單", icon: RiFileList3Line, actIcon: RiFileList3Line },
  // { keyWord: "iot", path: "/iot", title: "物聯裝置", icon: RiLoader2Line, actIcon: RiLoader2Line },
  { keyWord: "account", path: "/account", title: "帳號", icon: RiUserLine, actIcon: RiUserLine },
];

const matchesTab = (tab: (typeof tabButtons)[number], path: string) =>
  path.includes(tab.keyWord) || ("alsoMatch" in tab && path === tab.alsoMatch);

/** Skin V2：瓦斯訂購各步驟的標題（未列出者沿用 tab 名稱「瓦斯訂購」）。 */
/** Skin V2：標題列不放返回箭頭的頁面（底部已有「取消」等明確的返回動作）。 */
/**
 * Skin V2：覆寫 header 標題的頁面。
 * 只有「確認訂單」需要 —— 它是流程終點、語意與前幾步不同。其餘步驟維持「瓦斯訂購」，
 * 各自的步驟名放在卡片內的區塊標題（如配送時間頁）。
 * 刻意不做「瓦斯訂購 + 步驟名」兩層：兩個白字標題疊在藍色帶上會變成小標在上、大標在下。
 */
const GAS_V2_TITLES: Record<string, string> = {
  "/gasShop/cart": "確認訂單",
  // 帳號區的子頁：不覆寫的話會退回 tab 名稱「帳號」
  "/account/coupons": "優惠券",
  // 下單成功頁刻意留空：頁面正中央已經有一個大大的「訂單已送出」，
  // 標題列再放一次會變成同一句話在色帶上出現兩遍（左上一小、中間一大）。
  // 空字串而非移除這筆——移除會退回 tab 名稱「瓦斯訂購」，一樣是多的。
  "/gasShop/done": "",
};

const GAS_V2_NO_BACK = ["/gasShop/installPosition", "/gasShop/stations", "/gasShop/deliveryTime", "/gasShop/payment", "/gasShop/cart", "/gasShop/done"];

// 需要隱藏預設 Header 並設為 0 padding 的路徑
const HIDE_HEADER_PATHS = ["/home", "/account/paymentMethod", "/account/addAccount", "/account/paymentMethod/result"];

export default function PageWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  // 沒配到就當新客戶：按「瓦斯訂購」tab 先做首次加入設定（含 pendingReview），完成再進訂購。
  const { needIdentityCheck } = useKiosk();
  const needSetup = needIdentityCheck;
  const [setupOpen, setSetupOpen] = useState(false);
  const { chooseProduct, hideAmountOnPending, supplierIdentity, allowElectronicPay } = useOrder();
  const { userData, chooseIdentity } = useUser();

  // 載入目前 context 的草稿到購物車（原本由 TopBarTools 負責，UI 移除後改在此常駐）
  useCurrentDraftSync();

  const currentPath = location.pathname === "/" ? "/home" : location.pathname;
  const isKioskTab = currentPath === ORDER_ENTRY_PATH;
  // 訂單即時刷新的連線只有一條，掛在這裡：列表與詳情之間切換不必斷線重連，離開訂單頁就關
  useOrderRealtimeConnection({ active: isOrdersPath(currentPath) });
  const currentData = tabButtons.find((tab) => matchesTab(tab, currentPath));
  const isRootPath = tabButtons.some((tab) => tab.path === currentPath);
  // /pay/:orderId 自帶完整 Header（訂單付款卡），不可再套 PageWrapper 預設 Header，否則兩層藍色 header 疊版
  const isHideHeader = HIDE_HEADER_PATHS.includes(currentPath) || currentPath.startsWith("/pay/");
  // 一次性付款連結（Apple Pay 掃碼／外開 Safari）：無登入身分，底部 tab bar（首頁/訂購/訂單/帳號）
  // 一律導向需登入的頁面，此模式下顯示沒有意義，隱藏之。
  // 凍結掛載當下的判斷：PayLinkView 讀完 pt 會 replaceState 把它從網址列移除，若每次 render
  // 重新呼叫 isPayLinkMode()，tab bar 會在頁面跑到一半時忽然冒出來。
  const [isHideTabBar] = useState(() => isPayLinkMode());

  /**
   * V1 才用的捲動收合（改變 header 高度 → 內容區重排）。
   * V2 不走這條：標題列改成釘在頂端的疊層，身分／地址那塊移進捲動內容裡自然被捲走，
   * 捲動期間沒有任何 JS 與 layout 變化，順暢度不取決於效能調校。
   */
  const collapsible = currentPath.startsWith("/gasShop") && !isGasSkinV2();
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => setCollapsed(false), [currentPath]); // 換頁重置
  // 收合會讓 header 變矮 → 內容區變高 → 可捲距離變短，若內容不夠長會被夾回頂端而反覆收合/展開（彈跳）。
  // 故內容需明顯長於 header 縮小量（room > 220）且捲夠深（y > 120）才收合；已收合要回到近頂端才展開。
  /**
   * 捲動回報 → 標題區收合。
   *
   * 遲滯區間必須夠寬：收合會讓標題區矮約 60px、內容區變高，scrollTop 被瀏覽器夾回後
   * 若落回展開門檻以下就會展開，接著又超過收合門檻 → 來回震盪，手機上就是明顯的卡頓。
   * 故收合 80 / 展開 24，中間留 56px 緩衝。
   *
   * 另外用 rAF 節流：onScroll 在手機上每幀都來，直接跑 setState 會讓 React 在捲動中
   * 反覆進 reducer；這裡一幀只算一次，且值沒變就不呼叫 setState。
   */
  const scrollRafRef = useRef(0);
  /**
   * 內容是否還停在頂端。
   *
   * 給「頁首是滿版地圖」的頁面（訂單詳情配送中）用：停在頂端時標題列不畫底色，
   * 地圖才能一路鋪到畫面最上緣；一旦捲動，底色就浮回來——否則捲上來的卡片會直接
   * 穿過標題文字。底色本來就是為了擋住捲過去的內容，只有「底下確定是地圖」時才拿掉。
   */
  const [atTop, setAtTop] = useState(true);
  const handleScrollChange = (y: number, room: number) => {
    applyPinnedShrink(y);
    setAtTop((prev) => {
      const next = y <= 24;
      return next === prev ? prev : next;
    });
    if (!collapsible) return;
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = 0;
      setCollapsed((prev) => {
        const next = prev ? y > 24 : room > 220 && y > 120;
        return next === prev ? prev : next;
      });
    });
  };
  useEffect(() => () => cancelAnimationFrame(scrollRafRef.current), []);

  const headerCollapsed = collapsible && collapsed;
  // 瓦斯訂購換皮 V2（目前只在練習模式開啟）：標題置中、賣方身分另起一行，貼近參考稿的標題列。
  const skinV2 = isGasSkinV2();
  /** V2 頁首＝淡藍霧：頂端中央一團極淡的光，不是有色帶（見 all.scss 的 .gas-skin-v2-bg）。
      底夠亮，白字在上面讀不到，故頁首的標題／副標／身分／摘要／分頁籤一律改深色（見 useHazePage）。 */
  const hazePage = isHazePath(currentPath);
  /** subHeader 只有一行字的頁（優惠券）：底下緊接著分頁籤，外距要比一般頁收得多。 */
  const tightSubHeader = currentPath === "/account/coupons";
  /** 淡霧頁的標題色：深藍而非墨黑。
      淡藍霧上放黑字會把整區拉回中性（頁首就只剩「一段黑字」，藍白等於白做），
      用同一族的深藍才接得起來，也是這頁唯一還看得出品牌色的地方。 */
  const hazeInk = v2.hazeInk;
  const gasHeaderV2 = isSkinV2Path(currentPath) && skinV2;

  /**
   * V2 釘住的標題列高度：內容區要留出等高的上緣留白，內容才不會被壓在標題列底下。
   * 各頁標題列高度一致，但字體大小／安全區可能變動，故實測而非寫死。
   */
  const topBarRef = useRef<HTMLDivElement>(null);
  const [topBarH, setTopBarH] = useState(0);
  /**
   * 安裝位置頁的商品列（icon 瓦斯·20KG NT$700）要「縮小」而不是被捲走 ——
   * 客戶得隨時看得到自己在訂哪一桶。故把它放進釘住的標題列裡，捲動時用 transform: scale()
   * 縮小、同步縮短它的 height 把空間讓回來。
   *
   * 之所以可以每幀跑而不卡：標題列是 position:absolute（脫離文件流），改它的高度不會讓
   * 捲動內容重排；scale 又走合成層。而且全程直接寫 DOM style，不經過 React state，
   * 捲動期間 0 次 re-render。
   */
  const pinnedShrink = gasHeaderV2 && currentPath === "/gasShop/installPosition";
  /**
   * subHeader 要釘在標題列裡（而不是跟著內容捲）的頁面。
   *
   * 訂單詳情加進來的原因是版面而非捲動：配送中的訂單，上半頁整片是地圖，
   * 而「瓦斯行／訂單編號」原本排在內容的最前面，等於在地圖與標題列之間插一條橫帶，
   * 地圖就頂不到標題列下緣。收進標題列後，標題列以下就從地圖開始，
   * 這一列也順便變成永遠看得到（詳情頁只有一家瓦斯行、一個編號，釘著不會有歧義）。
   */
  const isOrderDetailPath = /^\/orders\/.+/.test(currentPath);
  /** 訂單詳情的頁首現在是滿版地圖（由配送追蹤元件回報，見 useTrackingHero） */
  const trackingHero = useTrackingHero((s) => s.active) && isOrderDetailPath;
  const pinnedSubHeader = pinnedShrink || (gasHeaderV2 && isOrderDetailPath);

  /**
   * 收合成 compact（縮圖 64→34、字級小一階、去掉外距），而不是對整列 scale()。
   * scale() 會連寬度一起縮，價格從右邊界飄到中間、字也被等比壓糊；compact 是版型本身
   * 換一套尺寸，滿版與右側對齊都留著，只有高度變矮。
   *
   * 一次布林切換，不是每幀寫 DOM：門檻用遲滯（捲過 48 收、回到 12 以內才展開）避免在
   * 臨界點來回跳；標題列是 absolute，換高度不會讓捲動內容重排，所以切換不影響捲動位置。
   */
  const [pinnedCompact, setPinnedCompact] = useState(false);
  // onScroll 每幀都來，用 ref 對照現值，只有真的越過門檻才呼叫 setState（捲動中 re-render 次數 0~1 次）
  const pinnedCompactRef = useRef(false);
  const applyPinnedShrink = (y: number) => {
    if (!pinnedShrink) return;
    const next = pinnedCompactRef.current ? y > 12 : y > 48;
    if (next === pinnedCompactRef.current) return;
    pinnedCompactRef.current = next;
    setPinnedCompact(next);
  };
  useEffect(() => {
    // 換頁重置
    pinnedCompactRef.current = false;
    setPinnedCompact(false);
  }, [currentPath]);
  useLayoutEffect(() => {
    const el = topBarRef.current;
    if (!el || !gasHeaderV2) return;
    const measure = () => setTopBarH(el.offsetHeight);
    measure();
    // 縮放頁的標題列高度每幀都被直接改寫，掛 ResizeObserver 會變成每幀 setState → 只量一次。
    if (pinnedShrink) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [gasHeaderV2, pinnedShrink, currentPath]);
  const headerTitle = (gasHeaderV2 ? GAS_V2_TITLES[currentPath] : undefined) ?? (currentData ? currentData.title : "");
  const showBack = !isRootPath && !(gasHeaderV2 && GAS_V2_NO_BACK.includes(currentPath));

  const activeTabIndex = tabButtons.findIndex((tab) => matchesTab(tab, currentPath));
  /**
   * 活動球／凸起的容器樣式：寬度剛好一個 tab 欄，位移用 transform（走合成層，不觸發 layout；
   * 若改動 left 會每幀重算版面並重跑 goo 濾鏡，切換就會卡）。
   */
  const slotStyle = (i: number) => ({
    width: `${100 / tabButtons.length}%`,
    transform: `translateX(${i * 100}%)`,
  });

  const handleTabPress = (value: string) => {
    // 下單漏斗入口：只有切到「瓦斯訂購」tab 才算進入下單流程，其餘 tab（首頁/訂單/帳號）純導覽不算
    if (value === ORDER_ENTRY_PATH) {
      track("entry_click", { entry_source: "tab" });
      if (needSetup) { setSetupOpen(true); return; } // 新客戶未完成加入設定 → 先做設定，不直接進訂購
    }
    navigate(value);
  };

  const handleBack = () => {
    /* 導頁時帶 state.from 的頁面（如從某個瓦斯行帳戶點進優惠券）優先回到來源頁——
       否則會退到該區的 tab 根目錄（/account），使用者等於被踢回上一層，剛才看的那個帳戶不見了。 */
    const from = (location.state as { from?: string } | null)?.from;
    if (from) {
      navigate(from);
      return;
    }
    const backRules: Array<{ match: string | RegExp; to: string }> = [
      { match: "/gasShop/installPosition", to: "/gasShop/products" },
      { match: "/gasShop/deliveryTime", to: "/gasShop/products" },
      { match: "/gasShop/payment", to: "/gasShop/deliveryTime" },
      // 電子付款開啟時中間多一步付款方式，確認訂單的上一步是它；關閉時直接回配送時間
      { match: "/gasShop/cart", to: allowElectronicPay ? "/gasShop/payment" : "/gasShop/deliveryTime" },
      { match: "/account/wallet", to: "/account" },
    ];
    const matched = backRules.find(({ match }) => (typeof match === "string" ? currentPath === match : match.test(currentPath)));
    if (matched) {
      navigate(matched.to);
      return;
    }
    const tab = tabButtons.find((tab) => matchesTab(tab, currentPath));
    if (tab) navigate(tab.path);
  };

  const renderSubHeader = () => {
    const subHeaderMap: Record<string, React.ReactNode> = {
      "/gasShop/products": (
        <>
          {/* 身分與地址是同一組脈絡（跟誰買、送到哪），彼此 10px；整組與底下的白卡才拉開 16px */}
          <XStack justifyContent="space-between" alignItems="center" mb={10}>
            <IdentitySwitchMenu />
            <MemberCircleList />
          </XStack>
          <AddressSwitchMenu themeColor={gasHeaderV2 ? "haze" : "white"} radius={gasHeaderV2 ? 20 : 16} />
        </>
      ),
      "/gasShop/stations": (
        <>
          <XStack justifyContent="space-between" alignItems="center">
            <IdentitySwitchMenu />
            <MemberCircleList />
          </XStack>
        </>
      ),
      "/gasShop/installPosition": (
        <GasProductTitle
          theme={gasHeaderV2 ? "haze" : "light"}
          title={`${chooseProduct?.display_name ? chooseProduct?.display_name : `${chooseProduct?.gas_type} · ${chooseProduct?.kilogram}KG`}`}
          desc=""
          price={chooseProduct?.unit_price || 0}
          kilogram={chooseProduct?.kilogram}
          hidePrice={hideAmountOnPending}
          compact={pinnedCompact}
        />
      ),
      "/gasShop/deliveryTime": gasHeaderV2 ? null : (
        <YStack my="$2">
          <GasStationInfo />
        </YStack>
      ),
      "/gasShop/payment": gasHeaderV2 ? null : (
        <YStack my="$2">
          <GasStationInfo />
        </YStack>
      ),
      "/gasShop/cart": gasHeaderV2 ? null : (
        <YStack my="$2">
          <GasStationInfo />
        </YStack>
      ),
      "/orders": <OrderListHeader />,
      "/account": (
        // V2：底下緊接著三格摘要，這裡的上下外距要收，否則招呼語與摘要之間空一大截
        <YStack my={gasHeaderV2 ? 2 : "$3"}>
          <UserProfileHeader user={userData} />
        </YStack>
      ),
      "/account/wallet": <WalletBalanceHeader balance={2000} />,
      "/account/coupons": (
        // V2：標題已在釘住的標題列，這裡只留說明；副標的白字提到 0.9 才讀得清（0.5 壓在藍上太淡）
        <YStack alignItems="flex-start">
          {!gasHeaderV2 && <TextCustom str="優惠券" type={TextSize.NORMAL} color={colors.light} bold={true} />}
          {/* V2 的副標刻意比標題小兩階且不加粗（原本 14 粗體、白 0.9，和 16 粗體的標題幾乎同級，
              兩行白字疊在藍帶上分不出主從）。字小了要提亮才讀得清，故 0.88 而非再壓暗。 */}
          <TextCustom
            str="結帳時自動套用最划算的優惠"
            type={gasHeaderV2 ? TextSize.TINY : TextSize.EXTRA_SMALL}
            color={gasHeaderV2 ? "#7d8895" : "rgba(255, 255, 255, 0.5)"}
            bold={!gasHeaderV2}
          />
        </YStack>
      ),
    };

    if (subHeaderMap[currentPath]) return subHeaderMap[currentPath];

    // 訂單詳情 /orders/:orderId → header 顯示訂單編號（order_list.order_id）+ 瓦斯行。
    // displayId 來源：列表/紀錄頁導頁時帶入，或由訂單詳情頁載入後回填 router state。
    // 不退回路由 id（那是 platform_order_id UUID，非使用者可讀編號）；尚未取得前先留空。
    if (/^\/orders\/.+/.test(currentPath)) {
      const navState = location.state as { displayId?: string; supplierName?: string | null } | null;
      const displayId = navState?.displayId;
      const id = displayId ?? "";
      // 優先用列表導頁帶入的 supplierName（詳情 API 不含瓦斯行名稱）；
      // 沒帶（如直接開網址）才退回目前身分的 primary_supplier。「全部」分頁 chooseIdentity 為 null，
      // 若沒有 supplierName 就會顯示空白，故由列表帶入避免瓦斯行資料消失。
      const supplierName = navState?.supplierName ?? chooseIdentity?.primary_supplier?.supplier_name;
      return (
        <XStack mt="$1" ai="center" jc="space-between" gap="$3">
          {supplierName ? (
            /* 不放瓦斯行字圖：這頁只有一家瓦斯行，字圖不用來區分任何東西（不像帳戶清單要一眼分辨多筆），
               放上去只是多一個色塊佔掉標題列的高度。名稱本身就夠識別。 */
            <XStack ai="center" gap="$2.5" flex={1} minWidth={0}>
              <YStack flex={1} minWidth={0}>
                <Text color={gasHeaderV2 ? v2.hazeSub : "rgba(255,255,255,0.85)"} fontSize={11}>
                  瓦斯行
                </Text>
                <Text color={gasHeaderV2 ? v2.hazeInk : colors.light} fontSize={14} fontWeight="700" numberOfLines={1}>
                  {supplierName}
                </Text>
              </YStack>
            </XStack>
          ) : (
            <View />
          )}
          <YStack ai="flex-end" flexShrink={0} maxWidth={170}>
            <Text color={gasHeaderV2 ? v2.hazeSub : "rgba(255,255,255,0.85)"} fontSize={11}>
              訂單編號
            </Text>
            <Text color={gasHeaderV2 ? v2.hazeInk : colors.light} fontSize={13} fontWeight="700" numberOfLines={1}>
              {id}
            </Text>
          </YStack>
        </XStack>
      );
    }

    return null;
  };

  const subHeaderNode = gasHeaderV2 ? renderSubHeader() : null;

  return (
    <View height="100dvh" width="100vw" backgroundColor={skinV2 ? "#f8fafc" : colors.light}>
      <Tabs
        value={currentPath}
        onValueChange={handleTabPress}
        orientation="horizontal"
        flexDirection="column"
        width="100%"
        flex={1}
        bg={skinV2 ? "#f8fafc" : colors.light}
        zIndex={zIndexes.sheet}>
        <View
          flex={1}
          position="relative"
          bg={gasHeaderV2 ? undefined : colors.bg}
          className={gasHeaderV2 ? "gas-skin-v2-bg" : undefined}>
          {isKioskTab ? (
            /* 點單機首頁自帶標題、捲動區與購物車浮動列，不套這裡的標題列與捲動容器（兩層捲動會互相吃手勢）。
               底部分頁列照常顯示——它是內容區的兄弟節點，購物車浮動列會停在它上方。
               isolation：點單機的步驟 sheet 是 zIndex 60 的絕對定位層，不隔開會蓋住分頁列中間凸起的球。 */
            <YStack flex={1} minHeight={0} position="relative" style={{ isolation: "isolate" }}>
              <Outlet />
            </YStack>
          ) : (
            <>
            {!isHideHeader &&
              (gasHeaderV2 ? (
                /* Skin V2：標題列釘在頂端（absolute），身分／地址那塊移進捲動內容裡自然被捲走。
                   這樣捲動時完全不改變任何元素高度 → 沒有重排、沒有 JS 介入，滑起來才會順。
                   標題列的背景畫在 .gas-skin-v2-topbar::before，與頁面容器同一張圖（同樣的
                   radial 座標與 250px 高度），所以看不出接縫。 */
                <div
                  ref={topBarRef}
                  className={`gas-skin-v2-topbar${trackingHero && atTop ? " is-over-hero" : ""}`}
                  style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 5, padding: "14px 16px 10px" }}>
                  <XStack justifyContent="space-between" alignItems="center">
                    <XStack alignItems="center">
                      {showBack && (
                        <View mr="$2" cursor="pointer" onPress={handleBack}>
                          <RiArrowLeftSLine size={20} color={hazePage ? hazeInk : colors.light} />
                        </View>
                      )}
                      {/* V2 標題比 V1 大一階（17）：底下的副標與分頁籤都縮小了，標題要撐得住這一區 */}
                      <TextCustom str={headerTitle} type={TextSize.SUBNORMAL} color={hazePage ? hazeInk : colors.light} bold={true} />
                    </XStack>
                    {/* 賣方身分：只在瓦斯訂購流程顯示——那裡才有「這單跟誰買」的問題。
                        訂單列表會混多家瓦斯行、帳號頁與特定瓦斯行無關，掛上去只是誤導；
                        下單成功頁是流程終點，單都送出了也不需要再標示賣方。
                        標題列只放名稱，統編與電話點名稱才展開（見 SupplierIdentityBar）。 */}
                    {isGasShopPath(currentPath) && currentPath !== "/gasShop/done" && supplierIdentity && (
                      <XStack flex={1} minWidth={0} jc="flex-end" ml="$3">
                        <SupplierIdentityBar identity={supplierIdentity} />
                      </XStack>
                    )}
                  </XStack>
                  {pinnedSubHeader && <div style={{ overflow: "hidden" }}>{renderSubHeader()}</div>}
                </div>
              ) : (
                <Header collapsed={headerCollapsed}>
                  <XStack justifyContent="space-between" alignItems="center" pb={headerCollapsed ? 0 : "$3"}>
                    <XStack alignItems="center">
                      {!isRootPath && (
                        <View mr="$2" cursor="pointer" onPress={handleBack}>
                          <RiArrowLeftSLine size={20} color={colors.light} />
                        </View>
                      )}
                      <TextCustom str={currentData ? currentData.title : ""} type={TextSize.SMALL} color={colors.light} bold={true} />
                    </XStack>
                    {/* 賣方身分（名稱／統編／電話）小字註記，靠右貼齊標題列。
                        刻意放在收合區外：捲動時藍色 header 收起，這行仍留著，客戶隨時查得到跟誰買。
                        空間不夠時只截瓦斯行名稱（見 SupplierIdentityBar）。 */}
                    {currentPath === "/gasShop/products" && supplierIdentity && (
                      <XStack flex={1} minWidth={0} jc="flex-end" ml="$3">
                        <SupplierIdentityBar identity={supplierIdentity} />
                      </XStack>
                    )}
                  </XStack>
                  {/* 渲染對應內容（gasShop 頁捲動時平滑收合，其餘維持原樣） */}
                  <div style={collapsible ? { maxHeight: collapsed ? 0 : 600, opacity: collapsed ? 0 : 1, overflow: "hidden", transition: "max-height 0.3s ease, opacity 0.2s ease" } : undefined}>
                    {renderSubHeader()}
                  </div>
                </Header>
              ))}
            <RadiusContentWrapper
              /* 訂單詳情自己吃內距（見 orderDetail 的內容容器）：配送中的地圖要左右滿版，
                 而內距在這一層的話，超出去的部分會被這個 ScrollView 的 overflow-x 裁掉。 */
              padding={isHideHeader || isOrderDetailPath ? "0" : "$4"}
              background={gasHeaderV2 ? "transparent" : undefined}
              /* 平常要讓開絕對定位的標題列；頁首是滿版地圖時不讓——
                 地圖要從畫面最上緣開始，標題列直接浮在它上面（底色由 .is-over-hero 拿掉）。 */
              contentContainerStyle={gasHeaderV2 ? { paddingTop: trackingHero ? 0 : Math.max(topBarH - 16, 0) } : undefined}
              // 付款方式卡帶頁：整頁剛好一個螢幕高、不捲動（卡帶依內容區高度排版）
              disableScroll={currentPath === "/account/addAccount" || currentPath === "/gasShop/payment"}
              onScrollChange={handleScrollChange}>
              {gasHeaderV2 ? (
                /* key 換頁時重播進場動畫。路由本來就會在換頁時換掉這棵子樹，
                   所以這個 key 不會製造額外的 remount。 */
                /* 錯開進場的兩種掛法：
                   瓦斯訂購認 .gas-stagger（卡片是共用元件 SkinCard，標得到）；
                   帳號頁的區塊是 Fragment 直接展開的，剛好就是這一層的直接子元素，
                   所以用 .gas-stagger-kids 從這裡數。
                   訂單頁不掛在這一層——它的卡片包在自己的容器裡，掛在這裡只會讓
                   「分頁籤 + 整份清單」當成一塊淡入；那一頁改在清單容器上掛（見 OrderList/index.tsx）。 */
                <YStack
                  key={isGasShopPath(currentPath) ? currentPath : "skin-v2"}
                  className={
                    isGasShopPath(currentPath)
                      ? "gas-page-stagger"
                      : currentPath.startsWith("/orders")
                        ? undefined
                        : "gas-stagger-kids"
                  }
                  flex={1}
                  minHeight={0}>
                  {/* 身分／地址（或各頁的 subHeader）現在是內容的一部分，捲動時自然離場 */}
                  {/* 優惠券頁的 subHeader 只有一行副標、底下緊接著分頁籤，外距要收（14px 會把整組拉散）；
                      其餘頁面的 subHeader 是身分／地址那組膠囊，底下接的是白卡——那是另一種物件，
                      要比膠囊彼此之間更開，否則整疊讀起來像同一塊。 */}
                  {!pinnedSubHeader && subHeaderNode && (
                    <YStack className="gas-stagger-lead" mb={tightSubHeader ? 6 : 16}>
                      {subHeaderNode}
                    </YStack>
                  )}
                  <Outlet />
                </YStack>
              ) : (
                <Outlet />
              )}
            </RadiusContentWrapper>
            </>
          )}
        </View>
        {!isHideTabBar &&
          (skinV2 ? (
            /* Skin V2：浮起的膠囊 tab bar，活動球在切換時滑動並與膠囊融合（見 all.scss 的 .gas-tabbar-*）。
               --soft：淡出拉長。只有瓦斯訂購流程有 sticky 的底部按鈕會被蓋到，
               其餘頁面拉長才不會把內容一刀切在 tab bar 上緣。 */
            <div className={`gas-tabbar-wrap${isGasShopPath(currentPath) || isKioskTab ? "" : " gas-tabbar-wrap--soft"}`}>
              <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
                <defs>
                  <filter id="gas-tab-goo">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="7" result="blur" />
                    <feColorMatrix in="blur" type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7" />
                  </filter>
                </defs>
              </svg>
              <div className="gas-tabbar">
                {/* 陰影獨立一層：放進 goo 濾鏡層會被吃掉 */}
                <div className="gas-tabbar-shadow" />
                <div className="gas-tabbar-goo">
                  <div className="gas-tabbar-pill" />
                  {activeTabIndex >= 0 && (
                    <div className="gas-tabbar-slot" style={slotStyle(activeTabIndex)}>
                      <div className="gas-tabbar-bump" />
                    </div>
                  )}
                </div>
                {activeTabIndex >= 0 && (
                  <div className="gas-tabbar-slot" style={slotStyle(activeTabIndex)}>
                    <div className="gas-tabbar-ball" />
                  </div>
                )}
                <div className="gas-tabbar-items">
                  {tabButtons.map((tab, i) => {
                    const isActive = i === activeTabIndex;
                    return (
                      <div
                        key={tab.path}
                        className="gas-tabbar-item"
                        role="button"
                        aria-label={tab.title}
                        aria-current={isActive ? "page" : undefined}
                        onClick={() => handleTabPress(tab.path)}>
                        <div className={isActive ? "gas-tabbar-icon is-active" : "gas-tabbar-icon"}>
                          <tab.icon size={20} color={isActive ? "#fff" : "#8A94A6"} />
                        </div>
                        <span className={isActive ? "gas-tabbar-label is-active" : "gas-tabbar-label"}>{tab.title}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <>
              <Separator borderColor={colors.borderLight} />
              <Tabs.List pt={6} pb="calc(6px + env(safe-area-inset-bottom))" bg={colors.light}>
                {tabButtons.map((tab) => {
                  const isActive = matchesTab(tab, currentPath);
                  return (
                    <Tabs.Tab
                      key={tab.path}
                      value={tab.path}
                      flex={1}
                      // onValueChange={(value: string) => navigate(value)}
                      bg="transparent"
                      hoverStyle={{ backgroundColor: "transparent" }}
                      pressStyle={{ backgroundColor: "transparent" }}
                      focusStyle={{ backgroundColor: "transparent" }}>
                      <YStack alignItems="center" gap={1}>
                        {isActive ? <tab.actIcon size={22} color={colors.primary} /> : <tab.icon size={22} color={colors.grayDark} />}
                        <TextCustom str={tab.title} type={TextSize.SUBTINY} color={isActive ? colors.primary : colors.grayDark} />
                      </YStack>
                    </Tabs.Tab>
                  );
                })}
              </Tabs.List>
            </>
          ))}
      </Tabs>
      <ShareSheet />

      {/* 傳單新客戶：按「瓦斯訂購」tab 時的首次加入設定，完成進訂購。
          skipIdentity：身分在 onboarding 就問完了（含「這不是我」的表態），這裡再問一次會拿同一個
          被拒絕的客戶重複詢問（IdentityCheckCard 會自行重打 match/by-phone）。鐵牌讀碼比對仍保留。 */}
      <FirstJoinSetup
        skipIdentity
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        onDone={() => { setSetupOpen(false); navigate(ORDER_ENTRY_PATH); }}
      />
    </View>
  );
}
